import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../config/firebase";
import LiveChat from "../components/LiveChat";
import AdminTopBar from "../components/AdminTopBar";
import SegmentBar from "../components/SegmentBar";
import AgendaBar from "../components/AgendaBar";
import QRCodeCanvasWrapper from "../components/QRCodeCanvasWrapper";
import SurveysView from "../components/SurveysView";
import StatisticsView from "../components/StatisticsView";
import AdminEventSettings from "../pages/AdminEventSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft, faChalkboardUser, faChartBar, faPoll,
  faGear, faCommentDots, faLayerGroup, faCalendarAlt, faQrcode, faLightbulb, faMugHot
} from "@fortawesome/free-solid-svg-icons";
import ExcelExporter from "../components/ExcelExporter";
import BreakModal from "../modals/BreakModal";

import Joyride, { STATUS } from "react-joyride"; // <<< TUTORIAL

const API = "https://api18081113.queroapp.pl";

// index kroku, na którym pokazujemy interaktywne pinowanie wiadomości
const PIN_STEP_INDEX = 2; // (0: topbar, 1: livechat panel, 2: demo wiadomości)

// --- CUSTOM TOOLTIP COMPONENT FOR JOYRIDE ---
const CustomTooltip = ({
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
  size,
}) => {
  return (
    <div {...tooltipProps} className="p-0 overflow-hidden tutorial-tooltip-animate" style={{ maxWidth: 450, backgroundColor: 'transparent' }}>
      {/* Heavy White Progress Bar */}
      <div className="tutorial-progress-bar m-0 mb-5" style={{ borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.1)', height: 10 }}>
        <div
          className="tutorial-progress-fill"
          style={{ width: `${((index + 1) / size) * 100}%`, height: 10, backgroundColor: '#fff', boxShadow: '0 0 20px rgba(255,255,255,0.6)' }}
        />
      </div>

      <div className="p-0">
        {/* Step Header */}
        <div className="mb-4">
          <div className="text-uppercase fw-bold mb-2" style={{ fontSize: '1rem', letterSpacing: '4px', color: 'rgba(255,255,255,0.8)', textShadow: '0 4px 8px rgba(0,0,0,0.8)' }}>
            Krok {index + 1} / {size}
          </div>
          <h1 className="fw-black m-0 text-white display-4" style={{ letterSpacing: '-2px', fontWeight: 900, textShadow: '0 4px 20px rgba(0,0,0,0.9)' }}>
            {step.title || "Instrukcja"}
          </h1>
        </div>

        {/* Content */}
        <div className="lh-base mb-5" style={{ fontSize: '1.6rem', color: '#fff', fontWeight: 600, lineHeight: 1.4, textShadow: '0 4px 15px rgba(0,0,0,0.9)' }}>
          {step.content}
        </div>

        {/* Footer Buttons */}
        <div className="d-flex align-items-center justify-content-between mt-5">
          <button {...skipProps} className="btn btn-link text-white opacity-50 fw-bold text-decoration-none p-0 shadow-none hover-opacity-100" style={{ fontSize: '1.2rem', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
            Pomiń samouczek
          </button>

          <div className="d-flex gap-4">
            {index > 0 && (
              <button {...backProps} className="btn btn-outline-light border-4 rounded-pill px-4 py-2 fw-black" style={{ border: '4px solid #fff' }}>
                Wstecz
              </button>
            )}
            <button {...primaryProps} className="btn btn-white bg-white text-dark rounded-pill px-5 py-3 fw-black shadow-lg" style={{ fontSize: '1.4rem', fontWeight: 900, border: 'none' }}>
              {isLastStep ? "GOTOWE" : "DALEJ"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [eventName, setEventName] = useState("");
  const [originalEventName, setOriginalEventName] = useState("");
  const [segments, setSegments] = useState([]); // płasko (legacy)
  const [groups, setGroups] = useState([]); // [{ key, group_id, master, subs }]
  const [agenda, setAgenda] = useState([]);
  const [agendaInput, setAgendaInput] = useState("");
  const [showAgendaInput, setShowAgendaInput] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [segmentInput, setSegmentInput] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showSurveys, setShowSurveys] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [settingsRefreshTrigger, setSettingsRefreshTrigger] = useState(0); // For forcing TopBar refresh on settings change
  const [showBreakModal, setShowBreakModal] = useState(false);

  const [loadingGroups, setLoadingGroups] = useState(true); // Start true for initial load

  // TUTORIAL – stan Joyride
  const [tourRun, setTourRun] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  // Active Stage State (Lifted from SegmentBar/LiveChat to fix instability)
  const [activeStage, setActiveStage] = useState(null);
  const [isActiveStageLoading, setIsActiveStageLoading] = useState(true);

  // Data Version for forcing refreshes in children
  const [dataVersion, setDataVersion] = useState(0);

  // Ograniczenie globalnego scrollowania do eliminacji podwójnego suwaka
  // document.body.style.overflow = "hidden" removed for "Prostota Max" to allow better accessibility
  useEffect(() => {
    // We handle scrolling within containers now
    return () => { };
  }, []);

  // SCROLLING BROWSER TITLE
  useEffect(() => {
    if (!eventName) return;

    const fullTitle = `${eventName} - SayQ App`;

    // If fits, just set it
    if (fullTitle.length < 30) {
      document.title = fullTitle;
      return;
    }

    // Scroll logic
    const scrollText = `${fullTitle}   `; // spacer
    let i = 0;

    const intervalId = setInterval(() => {
      document.title = scrollText.substring(i) + scrollText.substring(0, i);
      i = (i + 1) % scrollText.length;
    }, 300);

    return () => clearInterval(intervalId);
  }, [eventName]);

  const navigate = useNavigate();

  const getLS = (k) => {
    try {
      return localStorage.getItem(k) || "";
    } catch {
      return "";
    }
  };
  const secretId =
    getLS("secretId") || getLS("secredId") || getLS("secret-id") || getLS("secred-id");

  // Lifted fetchAll logic to be accessible for re-fetching
  const fetchAll = useCallback(async () => {
    const arrFrom = (x) =>
      Array.isArray(x) ? x : x?.segments || x?.data || x?.items || [];

    const normalizeRow = (r) => ({
      fullName: r?.segment_name ?? r?.name ?? r?.segment ?? "",
      group_id:
        r?.group_id === 0 || r?.group_id
          ? String(r.group_id)
          : r?.groupid
            ? String(r.groupid)
            : null,
    });

    if (!secretId) return navigate("/");

    // Ensure event segments table exists (important for social login users who skip the wizard)
    try {
      await fetch(`${API}/create-event-segments-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId }),
      });
    } catch (e) {
      console.warn("Failed to initialize event segments table", e);
    }

    // setLoadingGroups(true); // Maybe dont show loader on background refresh?
    try {
      const [eventRes, mastersRes, subsRes] = await Promise.all([
        fetch(`${API}/event-by-secret/${encodeURIComponent(secretId)}`),
        fetch(`${API}/segments-with-master/${encodeURIComponent(secretId)}`),
        fetch(`${API}/segments-without-master/${encodeURIComponent(secretId)}`),
      ]);

      if (!eventRes.ok) throw new Error("event-by-secret failed");
      const eventData = await eventRes.json();

      const mastersRaw = await mastersRes.json().catch(() => ({}));
      const subsRaw = await subsRes.json().catch(() => ({}));

      const masters = arrFrom(mastersRaw)
        .map(normalizeRow)
        .filter((x) => x.fullName);

      const subs = arrFrom(subsRaw)
        .map(normalizeRow)
        .filter((x) => x.fullName);

      const map = new Map();
      masters.forEach((m) => {
        const gid =
          m.group_id && m.group_id !== "null" && m.group_id !== "undefined"
            ? m.group_id
            : `__nogroup__:${m.fullName}`;
        if (!map.has(gid))
          map.set(gid, {
            key: gid,
            group_id: m.group_id,
            master: m.fullName,
            subs: [],
          });
        else map.set(gid, { ...map.get(gid), master: m.fullName });
      });

      subs.forEach((s) => {
        const gid = s.group_id ? String(s.group_id) : null;
        if (!gid) return;
        if (map.has(gid)) {
          map.get(gid).subs.push(s.fullName);
        }
      });

      const grouped = Array.from(map.values()).filter((g) => !!g.master);

      const flat = [...masters, ...subs].map((x) => x.fullName);
      const evName = eventData?.event_name || "";

      console.log("DEBUG: Dashboard loaded", {
        secretId,
        segmentsCount: flat.length,
        eventName: evName
      });

      // Safety check: if no segments exist, it's likely an unconfigured event (especially for social login)
      if (flat.length === 0) {
        if (evName.startsWith("Wydarzenie ") || evName === "" || evName === "Moje Wydarzenie") {
          console.log("Unconfigured event detected via segments count, redirecting to wizard...");
          return navigate(`/RegisterEvent/${secretId}`);
        }
      }

      setEventName(evName);
      setOriginalEventName(eventData?.event_name || "");
      setUserEmail(eventData?.mail || "");
      setSegments(flat);
      setGroups(grouped);
    } catch (err) {
      console.error("Błąd pobierania:", err);
      navigate("/");
    } finally {
      setLoadingGroups(false);
    }
  }, [navigate, secretId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]); // Dependency on fetchAll

  const refreshData = () => {
    fetchAll();
    setDataVersion(v => v + 1);
  };


  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem("secretId");
    localStorage.removeItem("secredId");
    localStorage.removeItem("eventName");
    navigate("/");
  };

  const handleSpeakersMode = () => {
    navigate("/speaker");
  };

  /* --------- Active Stage Logic --------- */
  useEffect(() => {
    if (!secretId) return;
    let alive = true;

    const fetchActive = async () => {
      try {
        const res = await fetch(`${API}/active-stage-from-event/${secretId}?t=${Date.now()}`, { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          if (alive) setActiveStage(data.activeStage || null);
        }
      } catch (e) {
        console.error("Failed to fetch active stage", e);
      } finally {
        if (alive) setIsActiveStageLoading(false);
      }
    };

    fetchActive();
    const interval = setInterval(fetchActive, 5000);

    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [secretId]);

  const updateActiveStage = async (newStage) => {
    // Optimistic update
    const previous = activeStage;
    setActiveStage(newStage);

    // Sync to LS immediately for other tabs (optional, but good for consistency)
    if (newStage) localStorage.setItem("ActiveStage", newStage);
    else localStorage.removeItem("ActiveStage");

    try {
      await fetch(`${API}/set-active-stage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId, activeStage: newStage }),
      });
      // Force TopBar to re-fetch settings (like break_active) AFTER the DB is updated
      setSettingsRefreshTrigger(prev => prev + 1);
    } catch (e) {
      console.error("Failed to set active stage", e);
      // Rollback on error? Maybe not needed for this UX, but worth noting.
      // setActiveStage(previous); 
    }
  };

  // TUTORIAL – definicja kroków
  const tourSteps = [
    {
      target: '[data-tour-id="admin-top-bar"]',
      title: "Górny Pasek Narzędzi",
      icon: faGear,
      content: (
        <div>
          <p>Tutaj zarządzasz nazwą wydarzenia oraz jego logo. Po prawej stronie znajdziesz przycisk <strong>WŁĄCZ PRZERWĘ</strong> – użyj go, gdy potrzebujesz momentu wytchnienia dla uczestników.</p>
        </div>
      ),
      disableBeacon: true,
      placement: "left-start",
      spotlightPadding: 0,
    },
    {
      target: '[data-tour-id="live-chat-column"]',
      title: "Panel Pytań (Czat)",
      icon: faCommentDots,
      content: (
        <div>
          <p>To serce interakcji. Tutaj pojawiają się pytania od Twoich gości. Każde ważne pytanie możesz <strong>PRZYPIĄĆ</strong>, aby prelegent widział je na swoim ekranie.</p>
        </div>
      ),
      placement: "right",
      spotlightPadding: 0,
    },
    {
      target: '[data-tour-id="segment-bar"]',
      title: "Etapy Wydarzenia",
      icon: faLayerGroup,
      content: (
        <div>
          <p>Tutaj kontrolujesz, co widzą goście. Kliknij <strong>WŁĄCZ</strong> przy danym etapie, aby aktywować czat i ankiety dla tej części spotkania.</p>
          <p className="small text-muted mt-1">Tylko jeden etap może być aktywny w danym momencie.</p>
        </div>
      ),
      placement: "left-start",
      spotlightPadding: 0,
    },
    {
      target: '[data-tour-id="agenda-bar"]',
      title: "Harmonogram (Agenda)",
      icon: faCalendarAlt,
      content: (
        <div>
          <p>Twój plan bitwy. Możesz go edytować w dowolnym momencie, klikając ikonę ołówka. Goście zawsze widzą aktualny plan na swoich telefonach.</p>
        </div>
      ),
      placement: "top",
      spotlightPadding: 0,
    },
    {
      target: '[data-tour-id="qrcode-wrapper"]',
      title: "Dostęp dla Gości",
      icon: faQrcode,
      content: (
        <div>
          <p>Wyświetl ten kod QR na rzutniku lub wydrukuj go. To najszybszy sposób, aby Twoi goście dołączyli do wydarzenia bez wpisywania skomplikowanych linków.</p>
        </div>
      ),
      placement: "left",
      spotlightPadding: 0,
    },
    {
      target: '[data-tour-id="right-column"]',
      title: "Narzędzia Dodatkowe",
      icon: faLightbulb,
      content: (
        <div>
          <p>Masz tutaj szybki dostęp do <strong>Trybu Prelegenta</strong> (widok dla prowadzącego), <strong>Ankiet</strong> oraz <strong>Statystyk</strong> z całego wydarzenia.</p>
        </div>
      ),
      placement: "left",
      spotlightPadding: 0,
    },
  ];

  // TUTORIAL – callback Joyride
  const handleTourCallback = (data) => {
    const { status, index, type } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setTourRun(false);
      setTourStepIndex(0);
    } else if (type === "step:after" || type === "target:notFound") {
      setTourStepIndex(index + 1);
    }
  };

  // callback z LiveChat po przypięciu wiadomości podczas tutorialu
  const handleTutorialMessagePinned = () => {
    if (!tourRun) return;
    if (tourStepIndex === PIN_STEP_INDEX) {
      setTourStepIndex((prev) => prev + 1);
    }
  };

  if (loadingGroups) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center vh-100 bg-light">
        <div className="q-spinner-box mb-4">
          <div className="q-spinner">
            <div className="q-mark" style={{ '--i': 0 }}>?</div>
            <div className="q-mark" style={{ '--i': 1 }}>?</div>
            <div className="q-mark" style={{ '--i': 2 }}>?</div>
          </div>
        </div>
        <h5 className="text-secondary fw-bold opacity-75 mt-3">Ładowanie wydarzenia...</h5>
        <style>{`
            .q-spinner-box { position: relative; width: 80px; height: 80px; }
            .q-spinner {
                position: absolute;
                inset: 0;
                animation: q-spin 3s linear infinite;
            }
            .q-mark {
                position: absolute;
                top: 0;
                left: 50%; /* Center horizontally */
                transform-origin: 50% 40px; /* Radius of rotation = 40px (half of box height?) No, height is 80px, center is 40px down. */
                /* Wait, if top is 0, dist to center (40px) is 40px. */
                margin-left: -12px; /* Half of width approx */
                width: 24px;
                height: 24px;
                
                color: #0d6efd;
                font-family: sans-serif;
                font-size: 32px;
                font-weight: 900;
                text-align: center;
                line-height: 1;
                
                /* Distribution */
                transform: rotate(calc(var(--i) * 120deg)) translateY(0px); /* translateY not needed if origin handles it? */
                /* transform-origin at "center of spinner" which is (12px, 40px) relative to element? */
                transform-origin: 50% 40px; 
            }
            /* Pulse animation on the character itself? Or scaling the item? */
            .q-mark {
                animation: q-pulse 1s ease-in-out infinite alternate;
                animation-delay: calc(var(--i) * -0.66s);
            }

            @keyframes q-spin {
                100% { transform: rotate(360deg); }
            }
            @keyframes q-pulse {
                0% { transform: rotate(calc(var(--i) * 120deg)) scale(0.8); opacity: 0.6; }
                100% { transform: rotate(calc(var(--i) * 120deg)) scale(1.3); opacity: 1; }
            }
         `}</style>
      </div>
    );
  }

  return (
    <div
      className="container-fluid m-0 p-0 d-flex flex-column overflow-hidden"
      style={{ height: '100dvh' }}
      data-tour-id="admin-layout"
    >
      {/* JOYRIDE – tutorial */}
      <Joyride
        steps={tourSteps}
        run={tourRun}
        stepIndex={tourStepIndex}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        callback={handleTourCallback}
        tooltipComponent={CustomTooltip}
        styles={{
          options: {
            zIndex: 10000,
            arrowColor: 'transparent',
          },
          spotlight: {
            borderRadius: 12,
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
          }
        }}
        floaterProps={{
          offset: 60,
        }}
      />

      {/* Przycisk do startu tutorialu (nie pokazujemy w trybie ustawień)
      {!showSettings && (
        <button
          onClick={() => {
            setTourStepIndex(0);
            setTourRun(true);
          }}
          className="btn btn-primary"
          style={{
            position: "fixed",
            right: "20px",
            bottom: "20px",
            zIndex: 12000,
          }}
          data-tour-id="start-tutorial"
        >
          Pokaż tutorial
        </button>
      )} */}

      {/* TOP BAR */}
      <AdminTopBar
        eventName={eventName}
        setEventName={setEventName}
        originalEventName={originalEventName}
        setOriginalEventName={setOriginalEventName}
        handleLogout={handleLogout}
        onShowSettings={() => setShowSettings(!showSettings)}
        showBackButton={showSettings}
        onBack={() => setShowSettings(false)}
        activeStage={activeStage}
        refreshTrigger={settingsRefreshTrigger}
        onShowBreak={() => setShowBreakModal(true)}
        onStartTutorial={() => setTourRun(true)}
      />


      {showSurveys && <SurveysView secretId={secretId} closeView={() => setShowSurveys(false)} />}
      {showStatistics && <StatisticsView secretId={secretId} eventName={eventName} closeView={() => setShowStatistics(false)} />}

      {
        showSettings ? (
          <div className="flex-grow-1 overflow-y-auto px-1 bg-light" >
            <AdminEventSettings onUpdate={() => setSettingsRefreshTrigger(prev => prev + 1)} />
          </div>
        ) : (
          <div className="row flex-grow-1 g-4 m-0 p-4 overflow-y-auto bg-soft" style={{ minHeight: 0 }}>
            {/* LEWA KOLUMNA – CZAT */}
            <div
              className="col-12 col-xl-4 col-md-12 mb-4 d-flex flex-column"
              style={{ minHeight: 'calc(100vh - 180px)' }}
              data-tour-id="live-chat-column"
            >
              <LiveChat
                activeStage={activeStage}
                onTutorialMessagePinned={handleTutorialMessagePinned}
                refreshTrigger={dataVersion}
              />
            </div>

            <div
              className="col-12 col-xl-5 col-md-12 d-flex flex-column gap-3 mb-4"
              data-tour-id="center-column"
            >
              <div data-tour-id="segment-bar">
                <SegmentBar
                  groups={groups}
                  loadingGroups={loadingGroups}
                  segments={segments}
                  setSegments={setSegments}
                  showInput={showInput}
                  setShowInput={setShowInput}
                  segmentInput={segmentInput}
                  setSegmentInput={setSegmentInput}

                  // New Props for State Lift
                  activeStage={activeStage}
                  onToggleStage={updateActiveStage}
                  onRefresh={refreshData}
                />
              </div>

              <div data-tour-id="agenda-bar">
                <AgendaBar
                  agenda={agenda}
                  setAgenda={setAgenda}
                  agendaInput={agendaInput}
                  setAgendaInput={setAgendaInput}
                  showAgendaInput={showAgendaInput}
                  setShowAgendaInput={setShowAgendaInput}
                />
              </div>
            </div>

            <div
              className="col-12 col-xl-3 col-md-12 d-flex flex-column gap-3"
              style={{ minWidth: 280 }}
              data-tour-id="right-column"
            >
              <div data-tour-id="qrcode-wrapper" className="bg-white shadow-md rounded-lg p-3 border border-light transition-standard">
                <QRCodeCanvasWrapper secretId={secretId} eventName={eventName} />
              </div>

              <div className="d-flex flex-column gap-2">
                <button
                  className="w-100 bg-white shadow-md rounded-lg p-3 d-flex align-items-center gap-3 border border-light transition-standard hover-lift"
                  onClick={handleSpeakersMode}
                  title="Otwórz widok prelegenta"
                >
                  <div className="rounded-circle bg-soft d-flex align-items-center justify-content-center text-primary" style={{ width: 40, height: 40 }}>
                    <FontAwesomeIcon icon={faChalkboardUser} className="fs-5" />
                  </div>
                  <div className="fw-bold text-dark text-start small">
                    Tryb Prelegenta
                  </div>
                </button>

                <button
                  className="w-100 bg-white shadow-md rounded-lg p-3 d-flex align-items-center gap-3 border border-light transition-standard hover-lift"
                  onClick={() => setShowSurveys(true)}
                  title="Otwórz ankiety"
                >
                  <div className="rounded-circle bg-soft d-flex align-items-center justify-content-center text-primary" style={{ width: 40, height: 40 }}>
                    <FontAwesomeIcon icon={faPoll} className="fs-5" />
                  </div>
                  <div className="fw-bold text-dark text-start small">
                    Ankiety
                  </div>
                </button>

                <button
                  className="w-100 bg-white shadow-md rounded-lg p-3 d-flex align-items-center gap-3 border border-light transition-standard hover-lift"
                  onClick={() => setShowStatistics(true)}
                  title="Statystyki i Raporty"
                  data-tour-id="excel-exporter"
                >
                  <div className="rounded-circle bg-soft d-flex align-items-center justify-content-center text-primary" style={{ width: 40, height: 40 }}>
                    <FontAwesomeIcon icon={faChartBar} className="fs-5" />
                  </div>
                  <div className="fw-bold text-dark text-start small">
                    Statystyki i Raporty
                  </div>
                </button>
              </div>

              <button
                className="w-100 btn btn-primary shadow-md rounded-lg p-3 d-flex align-items-center gap-3 border-0 transition-standard hover-lift text-white"
                onClick={() => setTourRun(true)}
                title="Włącz interaktywny samouczek"
                style={{ backgroundColor: 'var(--primary-color)' }}
              >
                <div className="rounded-circle bg-white d-flex align-items-center justify-content-center text-primary shadow-sm" style={{ width: 40, height: 40 }}>
                  <span className="fw-black fs-5">?</span>
                </div>
                <div className="fw-bold text-start small" style={{ letterSpacing: '0.3px' }}>
                  POMOC (INSTRUKCJA)
                </div>
              </button>
            </div>

            <style>{`
                  .ls-1 { letter-spacing: 0.5px; }
                  .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; }
                  .fw-black { font-weight: 900; }
               `}</style>
          </div>
        )
      }

      <footer className="w-100 text-center d-flex justify-content-between text-xs text-gray-500 py-3 border-t bg-white">
        <p></p>
        <p>2026® SayQ App all right reserved for <a href="https://www.linkedin.com/in/mateusz-krosnowski/" target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none fw-bold">Mateusz Krosnowski</a></p>
        <p className="px-3">v.1.3.6</p>
      </footer>

      <BreakModal
        isOpen={showBreakModal}
        onClose={() => setShowBreakModal(false)}
        onSuccess={() => setDataVersion(prev => prev + 1)}
      />
    </div>
  );
}
